import { memo, useCallback, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';

/**
 * Searchable picker to **add** a workflow tag by name. Selected tags are shown separately as chips.
 *
 * @param {object} props
 * @param {string[]} props.options - Tag names (unique, from workflow).
 * @param {string[]} [props.selectedTags] - Current tags (for option list + legacy values not in workflow).
 * @param {(tagName: string) => void} props.onAddTag - Called when user picks a tag from the list.
 * @param {boolean} [props.readOnly]
 * @param {'small' | 'medium'} [props.size]
 * @param {string} [props.id]
 * @param {string} [props.label]
 * @param {import('@mui/material').SxProps} [props.sx]
 */
function QcClassificationTagField({
  options,
  selectedTags = [],
  onAddTag,
  readOnly = false,
  size = 'small',
  id = 'qc-classification-tag',
  label = 'Add classification tag',
  sx,
}) {
  const [inputValue, setInputValue] = useState('');

  /** Tags not already on chips; keep legacy names in list only if not selected. */
  const optionList = useMemo(() => {
    const base = Array.isArray(options) ? [...options] : [];
    const selectedSet = new Set(
      (selectedTags || []).map((t) => String(t).trim()).filter(Boolean),
    );
    return base.filter((o) => !selectedSet.has(String(o).trim()));
  }, [options, selectedTags]);

  const baseSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#24389c',
      },
    },
  };

  const mergedSx = sx == null ? baseSx : [baseSx, sx];

  const filterOptions = useCallback((opts, state) => {
    const q = state.inputValue.trim().toLowerCase();
    if (!q) return opts;
    return opts.filter((o) => String(o).toLowerCase().includes(q));
  }, []);

  return (
    <Autocomplete
      id={id}
      fullWidth
      size={size}
      disabled={readOnly}
      options={optionList}
      value={null}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'reset') return;
        setInputValue(newInput ?? '');
      }}
      onChange={(_, newValue) => {
        if (newValue != null && String(newValue).trim() !== '') {
          onAddTag?.(String(newValue).trim());
          setInputValue('');
        }
      }}
      getOptionLabel={(opt) => (opt != null ? String(opt) : '')}
      isOptionEqualToValue={(a, b) => String(a) === String(b)}
      filterOptions={filterOptions}
      noOptionsText={
        optionList.length === 0
          ? Array.isArray(options) &&
            options.length > 0 &&
            (selectedTags?.length ?? 0) > 0
            ? 'All available tags are added'
            : 'No tags for this workflow'
          : 'No matching tags'
      }
      clearOnEscape
      selectOnFocus
      handleHomeEndKeys
      autoHighlight
      blurOnSelect
      slotProps={{
        popupIndicator: {
          onMouseDown: (e) => {
            e.preventDefault();
          },
        },
        popper: {
          placement: 'bottom-start',
          sx: (theme) => ({
            zIndex: theme.zIndex.modal + 100_001,
          }),
        },
        listbox: {
          // Keep focus (and caret/selection) in the transcript editor while picking an option.
          onMouseDown: (e) => {
            e.preventDefault();
          },
          sx: {
            maxHeight: { xs: 'min(50dvh, 280px)', sm: 320 },
            py: 0.5,
            textAlign: 'left',
            '& .MuiAutocomplete-option': {
              justifyContent: 'flex-start',
              textAlign: 'left',
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minHeight: { xs: 40, sm: 36 },
              py: 0.5,
            },
          },
        },
      }}
      sx={mergedSx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search tags…"
          margin="dense"
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          inputProps={{
            ...params.inputProps,
            'aria-label': `${label}. Type to search and select.`,
            autoComplete: 'off',
          }}
        />
      )}
    />
  );
}

export default memo(QcClassificationTagField);
